import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Avatar,
  Chip,
  CircularProgress,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import type { MissionDetail, MissionMessage } from '@/types/mission';
import { MissionService } from '@/services/mission/MissionService';
import { formatDateTime } from '@/utils/dateUtils';
import { getCallSignDisplayName } from '@/utils/generalUtils';

interface MessageTabProps {
  mission: MissionDetail;
}

export const MessageTab: React.FC<MessageTabProps> = ({ mission }) => {
  const theme = useTheme();
  const [messages, setMessages] = useState<MissionMessage[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchMessages = async () => {
      try {
        setIsLoading(true);
        const response = await MissionService.getMissionMessages(mission.id);
        setMessages(response.items);
        setError(null);
      } catch (err) {
        console.error('Failed to fetch messages:', err);
        setError('Failed to load messages.');
      } finally {
        setIsLoading(false);
      }
    };

    fetchMessages();
  }, [mission.id]);

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', padding: theme.spacing(4) }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  if (error) {
    return (
      <Typography variant="body2" sx={{ color: theme.palette.error.main }}>
        {error}
      </Typography>
    );
  }

  if (messages.length === 0) {
    return (
      <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
        No messages available.
      </Typography>
    );
  }

  const getSenderName = (message: MissionMessage) => {
    if (message.senderRole === 'Asset') {
      return getCallSignDisplayName(mission.assetCallSign, 'Asset');
    }
    if (message.senderRole === 'Ghost') {
      return getCallSignDisplayName(mission.ghostCallSign, 'Ghost');
    }
    return message.senderRole;
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: theme.spacing(2) }}>
      {messages.map((message) => {
        const isAsset = message.senderRole === 'Asset';
        const isGhost = message.senderRole === 'Ghost';
        const isAdmin = message.senderRole === 'Admin' || message.senderRole === 'System';

        return (
          <Box
            key={message.id}
            sx={{
              display: 'flex',
              flexDirection: isAsset ? 'row-reverse' : 'row',
              gap: theme.spacing(1.5),
              alignItems: 'flex-start',
            }}
          >
            {/* Avatar */}
            <Avatar
              sx={{
                width: 32,
                height: 32,
                bgcolor: isAsset
                  ? theme.palette.primary.main
                  : isGhost
                    ? theme.palette.mode === 'dark'
                      ? '#2e7d32'
                      : '#c8e6c9'
                    : theme.palette.grey[400],
                fontSize: theme.typography.body2.fontSize,
              }}
            >
              {getSenderName(message).charAt(0)}
            </Avatar>

            {/* Message Content */}
            <Box
              sx={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                gap: theme.spacing(0.5),
                alignItems: isAsset ? 'flex-end' : 'flex-start',
              }}
            >
              <Box sx={{ display: 'flex', gap: theme.spacing(1), alignItems: 'center' }}>
                {isGhost && (
                  <Chip
                    label="Ghost"
                    size="small"
                    sx={{
                      height: 18,
                      fontSize: '0.65rem',
                      backgroundColor: theme.palette.mode === 'dark'
                        ? theme.palette.grey[700]
                        : theme.palette.grey[300],
                      color: theme.palette.mode === 'dark'
                        ? theme.palette.grey[200]
                        : theme.palette.grey[800],
                      borderRadius: '4px',
                    }}
                  />
                )}
                {isAsset && (
                  <Chip
                    label="Asset"
                    size="small"
                    sx={{
                      height: 18,
                      fontSize: '0.65rem',
                      backgroundColor: theme.palette.mode === 'dark'
                        ? theme.palette.grey[700]
                        : theme.palette.grey[300],
                      color: theme.palette.mode === 'dark'
                        ? theme.palette.grey[200]
                        : theme.palette.grey[800],
                      borderRadius: '4px',
                    }}
                  />
                )}
                <Typography
                  variant="caption"
                  sx={{
                    fontWeight: 500,
                    color: theme.palette.text.secondary,
                    fontSize: '0.75rem',
                  }}
                >
                  {getSenderName(message)}
                </Typography>
              </Box>

              <Box
                sx={{
                  position: 'relative',
                  maxWidth: '70%',
                  ...(isAsset
                    ? {
                        // Right-side bubble: arrow pointing to speaker (right)
                        '&::before': {
                          content: '""',
                          position: 'absolute',
                          right: -6,
                          top: 12,
                          left: 'auto',
                          width: 0,
                          height: 0,
                          borderTop: '6px solid transparent',
                          borderBottom: '6px solid transparent',
                          borderLeft: `8px solid ${theme.palette.primary.main}`,
                          borderRight: 'none',
                          zIndex: 1,
                        },
                      }
                    : {
                        // Left-side bubble: arrow pointing to speaker (left)
                        '&::before': {
                          content: '""',
                          position: 'absolute',
                          left: -6,
                          top: 12,
                          width: 0,
                          height: 0,
                          borderTop: '6px solid transparent',
                          borderBottom: '6px solid transparent',
                          borderRight: isAdmin
                            ? `8px solid ${theme.palette.background.default}`
                            : theme.palette.mode === 'dark'
                              ? '8px solid #2e7d32'
                              : '8px solid #c8e6c9',
                          zIndex: 1,
                        },
                      }),
                }}
              >
                <Paper
                  elevation={0}
                  sx={{
                    padding: theme.spacing(1.5, 2),
                    backgroundColor: isAdmin
                      ? theme.palette.background.default
                      : isAsset
                        ? theme.palette.primary.main
                        : theme.palette.mode === 'dark'
                          ? '#2e7d32'
                          : '#c8e6c9',
                    color: isAdmin
                      ? theme.palette.text.secondary
                      : isAsset
                        ? theme.palette.primary.contrastText
                        : theme.palette.mode === 'dark'
                          ? 'rgba(255,255,255,0.95)'
                          : theme.palette.text.primary,
                    borderRadius: 2,
                    border: isAdmin ? `1px solid ${theme.palette.divider}` : 'none',
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      fontSize: theme.typography.body2.fontSize,
                      lineHeight: 1.5,
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-word',
                    }}
                  >
                    {message.content}
                  </Typography>
                </Paper>
              </Box>

              <Box sx={{ display: 'flex', gap: theme.spacing(1), alignItems: 'center' }}>
                <Typography
                  variant="caption"
                  sx={{
                    color: theme.palette.text.secondary,
                    fontSize: '0.75rem',
                  }}
                >
                  {formatDateTime(message.createdAt)}
                </Typography>
              </Box>
            </Box>
          </Box>
        );
      })}
    </Box>
  );
};
